import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

const PO_APPROVE_STAT_CARDS = [
  {
    key: 'totalCount',
    label: 'Total PO',
    titleColor: '#127aea',
    valueColor: '#2c7fd8',
    filterValue: null
  },
  {
    key: 'waitingForApprovalCount',
    label: 'Waiting for Approval',
    titleColor: '#7D6608',
    valueColor: '#D4AC0D',
    filterValue: 'Waiting for approval'
  },
  {
    key: 'approvedCount',
    label: 'Approved',
    titleColor: '#196F3D',
    valueColor: '#27AE60',
    filterValue: 'Approved'
  }
]

const DEFAULT_PO_APPROVE_STATS = PO_APPROVE_STAT_CARDS.reduce(
  (acc, card) => ({ ...acc, [card.key]: 0 }),
  {}
)

function normalizePoApproveStats(data) {
  if (!data || typeof data !== 'object') return { ...DEFAULT_PO_APPROVE_STATS }
  return PO_APPROVE_STAT_CARDS.reduce(
    (acc, card) => ({
      ...acc,
      [card.key]: Number(data[card.key]) || 0
    }),
    {}
  )
}

const PoApproveStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div
          className="stat-skeleton stat-skeleton-title"
          style={{ fontSize: '11px' }}
          aria-hidden="true"
        />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
)

const PoApproveStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${isActive ? ' procurement-stat-card-active' : ''
        }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor, fontSize: '11px' }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
)

const PO_STATUS_OPTIONS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Waiting for approval', label: 'Waiting for approval' },
  { value: 'Approved', label: 'Approved' }
]

const PO_SORT_COLUMN_MAP = {
  poNumber: 'poNumber',
  poDate: 'poDate',
  poDeliveryDate: 'poDeliveryDate',
  status: 'status',
  poStatus: 'poStatus',
  createdBy: 'createdBy'
}

const DEFAULT_SORT_BY = 'createdAt'
const DEFAULT_SORT_ORDER = 'desc'

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  if (
    obj.data &&
    typeof obj.data === 'object' &&
    typeof obj.data.data === 'string'
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function extractPosPayload(responseData) {
  const root = responseData
  let dataBlock = root?.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') {
    return { pos: [], pagination: {} }
  }

  if (Array.isArray(dataBlock)) {
    return {
      pos: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === 'object'
          ? root.pagination
          : {}
    }
  }

  let list =
    dataBlock.pos || dataBlock.poList || dataBlock.items || root.pos

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
    if (inner && typeof inner === 'object') {
      list = inner.pos || inner.poList || inner.items
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.pos == null &&
    dataBlock.poList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null || dataBlock.poNumber != null)
  ) {
    list = [dataBlock]
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {}

  if (typeof list === 'string') {
    list = parseJsonIfString(list)
  }

  const total =
    typeof pagination.total === 'number'
      ? pagination.total
      : typeof pagination.totalCount === 'number'
        ? pagination.totalCount
        : typeof dataBlock.totalCount === 'number'
          ? dataBlock.totalCount
          : typeof dataBlock.total === 'number'
            ? dataBlock.total
            : undefined

  return {
    pos: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === 'object' && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {}
  }
}

function formatPoDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str
  const dateOnlyMatch = str.match(/^(\d{2}-\d{2}-\d{4})/)
  if (dateOnlyMatch) return dateOnlyMatch[1]
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return d.toLocaleDateString()
}

function poNumber(po) {
  return po?.poNumber || po?.poNo || '-'
}

function poSummaryValue(po) {
  const s = po?.poSummary || po?.summary || ''
  return s.trim() ? s : '-'
}

function poCreatedByName(po) {
  const createdBy = po?.createdBy
  if (!createdBy) return po?.ownerName || '-'
  if (typeof createdBy === 'string') return createdBy.trim() || '-'
  return createdBy.name || createdBy.userName || po?.ownerName || '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function statusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'active') return 'badge-success'
  if (normalized === 'draft') return 'badge-secondary'
  return 'badge-info'
}

function renderPoRecordStatus(status) {
  const normalized = String(status || '').toLowerCase()
  const label = capitalizeStatus(status)

  if (normalized === 'inactive') {
    return (
      <span
        className="fwt_600 f_red"
        style={{ fontSize: '90%', fontWeight: 550 }}
      >
        {label}
      </span>
    )
  }

  if (normalized === 'active') {
    return (
      <span
        className="fwt_600 f_green"
        style={{ fontSize: '90%', fontWeight: 550 }}
      >
        {label}
      </span>
    )
  }

  return (
    <span
      className={`badge ${statusBadgeClass(status)}`}
      style={{ fontSize: '90%', fontWeight: 550 }}
    >
      {label}
    </span>
  )
}

function getPoId(po) {
  return po?._id || po?.id || ''
}

function PoApprove() {
  const { canView } = useModuleAccess('sales_approval')
  const savedFilters = JSON.parse(sessionStorage.getItem('poApproveFilters') || '{}')
  const [pos, setPos] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || '')
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || ''
  )
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(savedFilters.itemsPerPage || 25)
  const [poStatusFilter, setPoStatusFilter] = useState(savedFilters.poStatusFilter || '')
  const [activePoStatusFilter, setActivePoStatusFilter] = useState(
    savedFilters.activePoStatusFilter || ''
  )
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER
  )
  const [isPoCountLoading, setIsPoCountLoading] = useState(true)
  const [poStats, setPoStats] = useState({ ...DEFAULT_PO_APPROVE_STATS })

  const fetchApproverPoStatusCount = useCallback(async () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      setPoStats({ ...DEFAULT_PO_APPROVE_STATS })
      setIsPoCountLoading(false)
      return
    }

    try {
      setIsPoCountLoading(true)
      const response = await axios.post(API_URL, {
        action: 'get-approver-po-status-count',
        userid: userId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const statsBlock =
        responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : responseData

      if (isApiSuccess(responseData) || statsBlock?.totalCount != null) {
        setPoStats(normalizePoApproveStats(statsBlock))
      }
    } catch (error) {
      console.error('Fetch approver PO status count error:', error)
    } finally {
      setIsPoCountLoading(false)
    }
  }, [])

  const fetchApproverPos = useCallback(async () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setPos([])
      setTotalCount(0)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        action: 'get-approver-po',
        userid: userId,
        page: currentPage,
        limit: itemsPerPage
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : ''
      if (trimmedSearch) {
        payload.search = trimmedSearch
      }

      if (activePoStatusFilter) {
        payload.poStatus = activePoStatusFilter
      }

      if (sortBy) {
        payload.sortBy = sortBy
        payload.sortOrder = sortOrder
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const { pos: list, pagination } = extractPosPayload(responseData)

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0)

      if (ok) {
        setPos(list)
        const total =
          typeof pagination.total === 'number'
            ? pagination.total
            : typeof pagination.totalCount === 'number'
              ? pagination.totalCount
              : list.length
        setTotalCount(total)
      } else {
        toast.error(responseData?.message || 'Failed to fetch PO approval list', {
          autoClose: 2000
        })
        setPos([])
        setTotalCount(0)
      }
    } catch (error) {
      console.error('Fetch approver PO error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching PO approval list'
      toast.error(msg, { autoClose: 2000 })
      setPos([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activePoStatusFilter,
    sortBy,
    sortOrder
  ])

  useEffect(() => {
    fetchApproverPos()
  }, [fetchApproverPos])

  useEffect(() => {
    fetchApproverPoStatusCount()
  }, [fetchApproverPoStatusCount])

  useEffect(() => {
    sessionStorage.setItem(
      'poApproveFilters',
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        poStatusFilter,
        activePoStatusFilter,
        sortBy,
        sortOrder
      })
    )
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    poStatusFilter,
    activePoStatusFilter,
    sortBy,
    sortOrder
  ])

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1)
  const startItem = pos.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endItem = Math.min(currentPage * itemsPerPage, totalCount)

  const hasActiveFilters = Boolean(activeSearchQuery || activePoStatusFilter)

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  const handlePoStatusFilterChange = (e) => {
    const value = e.target.value
    setPoStatusFilter(value)
    setActivePoStatusFilter(value)
    if (currentPage !== 1) setCurrentPage(1)
  }

  const handleClearFilter = () => {
    setPoStatusFilter('')
    setActivePoStatusFilter('')
    if (currentPage !== 1) setCurrentPage(1)
  }

  const isStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activePoStatusFilter
    }
    return activePoStatusFilter === card.filterValue
  }

  const handleStatCardClick = (card) => {
    if (!card.filterValue) {
      setPoStatusFilter('')
      setActivePoStatusFilter('')
    } else {
      setPoStatusFilter(card.filterValue)
      setActivePoStatusFilter(card.filterValue)
    }

    if (currentPage !== 1) setCurrentPage(1)
  }

  const handleClearAll = () => {
    setSearchQuery('')
    setActiveSearchQuery('')
    handleClearFilter()
  }

  const handleSearchChange = (e) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    const trimmedValue = newValue ? newValue.trim() : ''
    if (trimmedValue === '' && activeSearchQuery !== '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : ''
    if (trimmedQuery === '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    } else {
      setActiveSearchQuery(trimmedQuery)
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchTrigger()
    }
  }

  const handleSort = (column) => {
    const apiField = PO_SORT_COLUMN_MAP[column] || column

    if (sortBy === apiField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc')
      } else {
        setSortBy(DEFAULT_SORT_BY)
        setSortOrder(DEFAULT_SORT_ORDER)
      }
    } else {
      setSortBy(apiField)
      setSortOrder('asc')
    }

    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const getSortIcon = (column) => {
    const apiField = PO_SORT_COLUMN_MAP[column] || column

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    if (sortOrder === 'asc') {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: '0.8em', flexShrink: 0 }}
      />
    )
  }

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...extraStyle
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  )

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 7
    const tp = totalPages

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i)
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    } else if (currentPage >= tp - 3) {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = tp - 4; i <= tp; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    }
    return pages
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">

            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">PO Approval</h1>
            </div>

            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/po"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                </li>
              </ol>
            </div>

          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row">
                {isPoCountLoading
                  ? PO_APPROVE_STAT_CARDS.map((card) => (
                    <PoApproveStatCardSkeleton key={card.key} />
                  ))
                  : PO_APPROVE_STAT_CARDS.map((card) => (
                    <PoApproveStatCard
                      key={card.key}
                      label={card.label}
                      value={poStats[card.key]}
                      titleColor={card.titleColor}
                      valueColor={card.valueColor}
                      isActive={isStatCardActive(card)}
                      onClick={() => handleStatCardClick(card)}
                    />
                  ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    <div className="col-md-4">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search purchase orders..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          PO Status
                        </label>
                        <select
                          className="form-control"
                          value={poStatusFilter}
                          onChange={handlePoStatusFilterChange}
                        >
                          <option value="">All PO Status</option>
                          {PO_STATUS_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-5 text-right">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1 d-block"
                          style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            visibility: 'hidden'
                          }}
                        >
                          Actions
                        </label>
                        {activePoStatusFilter ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleClearFilter}
                          >
                            <i className="fa-solid fa-times mr-1" />
                            Clear Filter
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : pos.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no purchase orders assigned for your approval.
                </div>
              ) : pos.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No purchase orders found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="poApprovalList"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: 'nowrap', minWidth: '35px' }}>
                            S.No
                          </th>
                          {renderSortableHeader('poNumber', 'PO No', {
                            minWidth: '110px'
                          })}
                          {renderSortableHeader('poDate', 'PO Date', {
                            minWidth: '100px'
                          })}
                          {renderSortableHeader('poDeliveryDate', 'Delivery Date', {
                            minWidth: '110px'
                          })}
                          <th style={{ minWidth: '180px' }}>Summary</th>
                          {renderSortableHeader('status', 'Status', {
                            minWidth: '80px'
                          })}
                          {renderSortableHeader('poStatus', 'PO Status', {
                            minWidth: '130px'
                          })}
                          <th style={{ minWidth: '120px' }}>Approver</th>
                          {renderSortableHeader('createdBy', 'Created User', {
                            minWidth: '130px'
                          })}
                          <th style={{ width: '60px', minWidth: '60px' }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pos.map((po, index) => {
                          const pid = getPoId(po)
                          return (
                            <tr key={pid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {poNumber(po)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {formatPoDate(po.poDate)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {formatPoDate(po.poDeliveryDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: '240px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal'
                                }}
                              >
                                {poSummaryValue(po)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {renderPoRecordStatus(po.status)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {po.poStatus || '-'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {po.approverName || po.approverDetails?.name || '-'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {poCreatedByName(po)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: 'nowrap' }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <Link
                                      to={`/poApproveView/${pid}`}
                                      className="crsr_pntr"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && pos.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: '13px' }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: '13px' }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === 'ellipsis' ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${currentPage === page ? 'active' : ''}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li
                              className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PoApprove
